<script>
// Casca de integração entre o tour guiado (mecânica, em @modules/global) e a
// Jornada de Configuração (dado). Lê o slice, entrega os passos ao host,
// marca a etapa ao fim e oferece a próxima.
//
// Montado UMA vez em `main.vue`, ao lado do CentralConfigGuideDrawer, pelo
// mesmo motivo dele: seis das treze etapas do Fee Fixo navegam entre telas no
// meio do fluxo, e um tour montado numa view morreria no router.push.
//
// Não conhece nenhum módulo: os seletores chegam prontos no slice, postos lá
// por quem inicia o tour (o card da Home, o shell do módulo). É o que mantém
// central-config → fee-fixo como dependência inexistente.
import { GlobalBus } from '@services/bus'
import { apiCentralConfig } from '@modules/central-config/api/apiCentralConfig'
import { CENTRAL_CONFIG_TOUR_PROXIMA_ETAPA } from '@modules/central-config/constants/Text'
import { rotaPertenceAoModulo } from '@modules/central-config/utils/tour-escopo-de-rota'
import GuidedTourHost from '@modules/global/components/guided-tour/guided-tour-host.vue'

// Mesmo piso de spinner das outras superfícies de marcação (o POST resolve
// rápido demais e o spinner pisca).
const MIN_LOADING_MS = 600

export default {
  name: 'CentralConfigTourGuiado',

  components: { GuidedTourHost },

  data() {
    return {
      marcando: false,
      reancorarToken: 0,
      canalOuvido: null,
      aoAbrirModal: null,
      canalDeAvancoOuvido: null,
      aoConcluirAcao: null,
    }
  },

  computed: {
    // Guard no padrão do módulo (`this.$store &&`): o Vuex do central-config é
    // registrado dinamicamente, e um consumidor montado antes do registro leria
    // `undefined` e estouraria no `.ativo` logo abaixo.
    contexto() {
      const getters = (this.$store && this.$store.getters) || {}
      return getters['centralConfig/tourContexto'] || { ativo: false }
    },

    ativo() {
      return !!this.contexto.ativo
    },

    passos() {
      return this.contexto.passos || []
    },

    indice() {
      return this.contexto.indice || 0
    },

    fase() {
      return this.contexto.fase || 'passos'
    },

    marcarConcluida() {
      return this.contexto.marcarConcluida !== false
    },

    passoAtual() {
      if (!this.ativo) return null
      return this.passos[this.indice] || null
    },

    // Canal do GlobalBus que faz o alvo do passo atual existir (modal).
    canalDoPasso() {
      const passo = this.passoAtual
      return (passo && passo.aguarda) || null
    },

    // Canal que CONCLUI o passo atual — o app avisando que a ação terminou.
    canalDeAvanco() {
      const passo = this.passoAtual
      return (passo && passo.avancaEm) || null
    },

    drawerAberto() {
      const getters = (this.$store && this.$store.getters) || {}
      return !!getters['centralConfig/guidePanelOpen']
    },

    // A próxima etapa é resolvida por quem iniciou o tour e guardada no slice:
    // é ela que carrega os seletores da etapa seguinte.
    proximaEtapa() {
      return this.contexto.proximaEtapa || null
    },
  },

  watch: {
    canalDoPasso: 'ouvirCanalDoPasso',
    canalDeAvanco: 'ouvirCanalDeAvanco',

    ativo(agora) {
      if (agora) {
        this.ouvirCanalDoPasso()
        this.ouvirCanalDeAvanco()
        return
      }
      this.pararDeOuvirCanal()
      this.pararDeOuvirAvanco()
    },

    // O tour pertence a UM módulo, mas o host mora no layout global e sobrevive
    // a qualquer navegação. Sem isto, quem estava no passo 3 do Fee Fixo e
    // clicava no menu lateral levava o overlay junto — véu de tela cheia sobre
    // o CRM, com um balão descrevendo uma tela ausente (achado em review).
    //
    // Encerrar (e não só esconder) é o certo: fora do módulo o assinante de
    // `EVENTO_PROXIMA_ETAPA` já foi desligado com o shell, então o desfecho
    // marcaria a etapa como concluída e não encadearia nada.
    $route: 'encerrarSeSaiuDoModulo',
  },

  mounted() {
    this.aoAbrirModal = () => {
      this.reancorarToken += 1
    }
    // O índice do passo é lido na hora do evento, não capturado agora: o
    // listener é religado a cada troca de passo, mas um evento em voo do passo
    // anterior chegaria com o índice velho e pularia dois de uma vez.
    this.aoConcluirAcao = () => {
      if (this.ativo && this.canalDeAvanco) this.avancar()
    }
    if (this.ativo) {
      this.ouvirCanalDoPasso()
      this.ouvirCanalDeAvanco()
    }
  },

  beforeDestroy() {
    this.pararDeOuvirCanal()
    this.pararDeOuvirAvanco()
    this.aoAbrirModal = null
    this.aoConcluirAcao = null
  },

  methods: {
    // O alvo do passo pode viver dentro de um modal que ainda não abriu. Em vez
    // de esticar o retry (que rodaria a esmo por minutos), reagimos ao evento
    // que abre o modal — o mesmo canal que a tela usa.
    ouvirCanalDoPasso() {
      this.pararDeOuvirCanal()
      if (!this.ativo || !this.canalDoPasso || !this.aoAbrirModal) return
      this.canalOuvido = this.canalDoPasso
      GlobalBus.$on(this.canalOuvido, this.aoAbrirModal)
    },

    pararDeOuvirCanal() {
      if (!this.canalOuvido || !this.aoAbrirModal) return
      GlobalBus.$off(this.canalOuvido, this.aoAbrirModal)
      this.canalOuvido = null
    },

    // Passo cuja ação não termina num clique no alvo (subir arquivo, salvar):
    // quem sabe que acabou é a tela, e ela já avisa pelo mesmo canal que usa
    // para recarregar a lista. O tour só escuta — nenhuma tela precisou mudar.
    ouvirCanalDeAvanco() {
      this.pararDeOuvirAvanco()
      if (!this.ativo || !this.canalDeAvanco || !this.aoConcluirAcao) return
      this.canalDeAvancoOuvido = this.canalDeAvanco
      GlobalBus.$on(this.canalDeAvancoOuvido, this.aoConcluirAcao)
    },

    pararDeOuvirAvanco() {
      if (!this.canalDeAvancoOuvido || !this.aoConcluirAcao) return
      GlobalBus.$off(this.canalDeAvancoOuvido, this.aoConcluirAcao)
      this.canalDeAvancoOuvido = null
    },

    /**
     * Mostra ou esconde o passo a passo em TEXTO sem sair do tour.
     *
     * As duas apresentações da mesma etapa passam a conviver: o spotlight diz
     * ONDE clicar, o drawer diz a etapa inteira. O tour arranca recolhendo o
     * drawer (`iniciarTourSePossivel`) porque ele empurra o layout em 380px —
     * mas isso deixou de ser impedimento desde 14/08, quando o host passou a
     * reancorar sozinho a cada mudança de DOM e a cada fim de transição. Quem
     * quiser ler agora tem por onde, sem precisar encerrar o tour.
     */
    alternarDrawer() {
      const acao = this.drawerAberto ? 'collapseGuide' : 'expandGuide'
      this.$store.dispatch(`centralConfig/${acao}`)
    },

    avancar() {
      const proximo = this.indice + 1
      if (proximo < this.passos.length) {
        this.$store.dispatch('centralConfig/irParaPassoDoTour', proximo)
        return
      }
      this.$store.dispatch('centralConfig/concluirPassosDoTour')
    },

    voltar() {
      if (this.indice <= 0) return
      this.$store.dispatch('centralConfig/irParaPassoDoTour', this.indice - 1)
    },

    definirMarcacao(marcar) {
      this.$store.dispatch('centralConfig/definirMarcacaoDoTour', marcar)
    },

    // Encerrar NO MEIO nunca marca nada: meio tour não é declaração de
    // "eu fiz isto" por nenhuma leitura.
    async fechar() {
      if (this.fase === 'desfecho') {
        await this.marcarSePedido()
      }
      this.encerrar()
    },

    async irParaProximaEtapa() {
      const proxima = this.proximaEtapa
      await this.marcarSePedido()
      this.encerrar()
      if (!proxima) return
      // Quem sabe iniciar a próxima é quem tem o mapa de seletores — o emissor
      // do evento (o card/shell do módulo) monta os passos e despacha o tour.
      GlobalBus.$emit(CENTRAL_CONFIG_TOUR_PROXIMA_ETAPA, proxima)
    },

    async marcarSePedido() {
      if (!this.marcarConcluida || this.marcando) return
      const taskId = this.contexto.taskId
      if (!taskId) return

      this.marcando = true
      const espera = new Promise((resolve) =>
        setTimeout(resolve, MIN_LOADING_MS)
      )
      try {
        // marcarTask já re-consulta a jornada internamente — não chamar
        // carregarJornada de novo aqui.
        const r = await apiCentralConfig.marcarTask({ taskId, done: true })
        this.$showSnack(r)
      } finally {
        await espera
        this.marcando = false
      }
    },

    encerrar() {
      // Reexpande o drawer da Jornada só se foi o tour quem o recolheu — quem
      // já estava com o FAB antes de começar continua com o FAB.
      if (this.contexto.recolheuGuia) {
        this.$store.dispatch('centralConfig/expandGuide')
      }
      this.$store.dispatch('centralConfig/encerrarTour')
    },

    // Navegar DENTRO do módulo é o fluxo normal do tour (uma etapa atravessa
    // Clientes → Simulação), então só encerra quando a rota sai de vez. A regra
    // mora num util puro e é fail-open — ver `tour-escopo-de-rota.js`.
    encerrarSeSaiuDoModulo() {
      if (!this.ativo) return
      const nome = (this.$route && this.$route.name) || ''
      if (rotaPertenceAoModulo(this.contexto.moduleKey, nome)) return
      this.encerrar()
    },
  },
}
</script>

<template>
  <GuidedTourHost
    :ativo="ativo"
    :passos="passos"
    :indice="indice"
    :fase="fase"
    :proxima-etapa="proximaEtapa"
    :marcando="marcando"
    :marcar-concluida="marcarConcluida"
    :reancorar-token="reancorarToken"
    :drawer-aberto="drawerAberto"
    @avancar="avancar"
    @voltar="voltar"
    @fechar="fechar"
    @alternar-drawer="alternarDrawer"
    @proxima-etapa="irParaProximaEtapa"
    @update:marcarConcluida="definirMarcacao"
  />
</template>
